import { useCallback, useMemo } from 'react';
import { DrawingData } from '../utils/drawingExport';
import {
  drawingStore,
  isPersistenceUnavailable,
  markPersistenceUnavailable,
  PERSISTED_ENTRY_TTL_MS,
} from '../services/persistence';

type PersistedDrawing = {
  drawingData: DrawingData;
  savedAt: number;
};

interface DrawingPersistenceOptions {
  gameKey: string;
  stackId: string;
  entryNumber: number;
}

const buildStorageKey = ({ gameKey, stackId, entryNumber }: DrawingPersistenceOptions) =>
  `drawing:${gameKey}:${stackId}:${entryNumber}`;

export const useDrawingPersistence = ({ gameKey, stackId, entryNumber }: DrawingPersistenceOptions) => {
  const storageKey = useMemo(
    () => buildStorageKey({ gameKey, stackId, entryNumber }),
    [gameKey, stackId, entryNumber]
  );

  const loadDrawing = useCallback(async () => {
    let saved: PersistedDrawing | { dataUrl?: string; savedAt?: number } | null;
    try {
      saved = await drawingStore.getItem<PersistedDrawing | { dataUrl?: string; savedAt?: number }>(storageKey);
    } catch (error) {
      markPersistenceUnavailable(error);
      return null;
    }
    if (!saved || !saved.savedAt) {
      return null;
    }

    if (Date.now() - saved.savedAt > PERSISTED_ENTRY_TTL_MS) {
      await drawingStore.removeItem(storageKey);
      return null;
    }

    if ('drawingData' in saved && saved.drawingData) {
      return saved.drawingData;
    }

    await drawingStore.removeItem(storageKey);
    return null;
  }, [storageKey]);

  const saveDrawing = useCallback(
    async (drawingData: DrawingData) => {
      if (!drawingData || isPersistenceUnavailable()) return;
      const payload: PersistedDrawing = {
        drawingData,
        savedAt: Date.now(),
      };
      try {
        await drawingStore.setItem(storageKey, payload);
      } catch (error) {
        markPersistenceUnavailable(error);
      }
    },
    [storageKey]
  );

  const clearDrawing = useCallback(async () => {
    try {
      await drawingStore.removeItem(storageKey);
    } catch (error) {
      markPersistenceUnavailable(error);
    }
  }, [storageKey]);

  return {
    storageKey,
    loadDrawing,
    saveDrawing,
    clearDrawing,
  };
};
